import {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useState,
    type JSX,
} from 'react';
import { useUpdateEffect } from 'ahooks';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { Category } from '@archipro-design/aria';

import { Sheet, SheetContent } from '@rocco/components/sheet';
import { PillButton } from '@rocco/components/pill-button';
import { cn } from '@rocco/utils/cn';
import { useFeature } from '@modules/root';
import { useRootPermissions } from '@modules/user';
import { useFilterStates } from '@modules/search/hook/use-filter-states';
import type { SearchIndex } from '@modules/search/type';
import type {
    FilterState,
    FilterStates,
    FilterStatesKey,
} from '@modules/search/type/filter-state';
import { resetFilterStates } from '@modules/search/util/reset-filter-states';
import type {
    FilterSection,
    FilterSectionFilterType,
} from '@modules/search/components/filter-drawer/FilterDrawer';
import type { SortOption } from '@modules/search/hook/use-search-sorts';
import { useSearchSorts } from '@modules/search/hook/use-search-sorts';
import { getAppliedFilterItems } from '~/modules/search/util/get-applied-filter-items';
import { useCurrentLocation } from '~/modules/location/hook/use-current-location';
import { createFacetFilterSections } from '@modules/search/util/create-facet-filters';
import type { CategoryFilterState } from '@modules/search/components/filter-drawer/FilterCategorySelector';
import { renderRefineControl } from './ProfessionalFilterControls';
import { useLogFilterOpen } from '~/modules-rocco/filters/tracking/use-log-filter-open';
import { useSortControlsEnabled } from '@modules/search/config/sort-controls';

interface ProfessionalFilterSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    filterSections: FilterSection[];
    filterStates: FilterStates;
    category?: SearchIndex;
    /** Applies the staged filter states to the tab (navigates + closes). */
    onApplyFilters?: (states: FilterStates) => void;
    ignoreFilterKeys?: FilterStatesKey[];
}

/** Singular / plural result nouns for the "Show N …" CTA, keyed by index. */
const RESULT_LABEL: Partial<Record<SearchIndex, [string, string]>> = {
    product: ['Product', 'Products'],
    project: ['Project', 'Projects'],
    professional: ['Professional', 'Professionals'],
    blogpost: ['Article', 'Articles'],
};

/** Root crumb label for the category breadcrumb, keyed by index. */
const ALL_LABEL: Partial<Record<SearchIndex, string>> = {
    product: 'All Products',
    project: 'All Projects',
    professional: 'All Network',
    blogpost: 'All Articles',
};

interface RefineSection {
    key: string;
    title: string;
    /** The rendered filter control, shown in the drill-in sub-view. */
    content: JSX.Element;
    /** Count of applied values, shown muted next to the chevron. */
    appliedCount: number;
}

/** Searchable lists that show their search box (the category does), overriding
 * the config's hideSearch. */
const SEARCH_ENABLED_KEYS = new Set<FilterStatesKey>(['brands', 'designers']);

/** Section-title overrides to match the category Refine wording. */
const SECTION_TITLE_RELABEL: Record<string, string> = { Specify: 'Downloads' };

/** Checkbox label overrides to match the category Refine wording. */
const CHECKBOX_LABEL_RELABEL: Partial<Record<FilterStatesKey, string>> = {
    isPurchasable: 'Shop',
};

/** Category-page filter order (post-relabel), after any staff-only sections;
 * sections not listed keep their config order after these. */
const REFINE_SECTION_ORDER = [
    'Pricing',
    'Availability',
    'Downloads',
    'Colour',
    'Material',
    'Brand',
    'Designer',
    'Size',
    'Suppliers',
];

/** Applies the category-parity overrides (search box, labels) to a filter. */
const normalizeRefineFilter = (
    filter: FilterSectionFilterType,
): FilterSectionFilterType => {
    if (filter.kind === 'FilterGroup') {
        return {
            ...filter,
            filters: filter.filters.map(normalizeRefineFilter),
        };
    }
    if (
        filter.kind === 'SearchList' &&
        SEARCH_ENABLED_KEYS.has(filter.filterKey)
    ) {
        return { ...filter, props: { ...filter.props, hideSearch: false } };
    }
    if (
        filter.kind === 'Checkbox' &&
        CHECKBOX_LABEL_RELABEL[filter.filterKey]
    ) {
        return {
            ...filter,
            props: {
                ...filter.props,
                label: CHECKBOX_LABEL_RELABEL[filter.filterKey],
            },
        };
    }
    return filter;
};

/** Relabels + reorders the config sections to match the category Refine. */
const normalizeRefineSections = (sections: FilterSection[]): FilterSection[] =>
    sections
        .map((section, index) => ({
            section: {
                ...section,
                title: SECTION_TITLE_RELABEL[section.title] ?? section.title,
                filter: normalizeRefineFilter(section.filter),
            },
            index,
        }))
        .sort((a, b) => {
            // Staff-only sections lead the sheet, apart from the reader's.
            const staffFirst =
                Number(Boolean(b.section.adminOnly)) -
                Number(Boolean(a.section.adminOnly));
            if (staffFirst !== 0) return staffFirst;
            const orderOf = (title: string) => {
                const at = REFINE_SECTION_ORDER.indexOf(title);
                return at === -1 ? Number.MAX_SAFE_INTEGER : at;
            };
            const delta = orderOf(a.section.title) - orderOf(b.section.title);
            return delta !== 0 ? delta : a.index - b.index;
        })
        .map(({ section }) => section);

/**
 * The professional grid tabs' mobile filter, styled to match the category
 * page's "Refine" decision-support sheet: a rocco bottom {@link Sheet} with a
 * "Refine" header, single-select sort pills, drill-in filter rows and a
 * "Show N Products" commit CTA. It runs on the professional tabs' OWN filter
 * data (the modules {@link useFilterStates} staging + the rocco-based refine
 * controls), so the result counts stay scoped to this professional and the
 * category filtering stays in-professional — only the presentation matches the
 * rocco Refine sheet, not its data stack. Staged: toggles preview the count on
 * the CTA, which applies them and closes.
 */
/** Aria `space-N` gap tokens (N raw px) are only understood by Aria `<Flex>`;
 *  a plain flex div needs a real CSS length, so resolve `space-8` → `8px`. */
const resolveFlexGap = (gap: unknown): string | undefined => {
    if (typeof gap !== 'string') return undefined;
    const token = /^space-(\d+)$/.exec(gap);
    return token ? `${token[1]}px` : gap;
};

/** The path from a category tree root down to the node with `targetId`, walking
 *  `.children` (fully populated by convertSearchCategory) rather than the
 *  one-level `.parent` stub — so deep selections keep every breadcrumb. */
const findCategoryPath = (
    node: Category,
    targetId: Category['id'],
    trail: Category[] = [],
): Category[] | null => {
    const path = [...trail, node];
    if (node.id === targetId) return path;
    for (const child of node.children ?? []) {
        const found = findCategoryPath(child, targetId, path);
        if (found) return found;
    }
    return null;
};

export const ProfessionalFilterSheet = ({
    open,
    onOpenChange,
    filterSections,
    filterStates: initialStates,
    category,
    onApplyFilters,
    ignoreFilterKeys,
}: ProfessionalFilterSheetProps) => {
    const enableAwardBadges = useFeature('award_badges');
    const { marketingView } = useRootPermissions();
    const sortControlsEnabled = useSortControlsEnabled();

    const {
        loading: aggregateLoading,
        filterStates,
        updateFilter,
        resetFilters,
        aggregate,
    } = useFilterStates({ initialStates, category });

    const [drillKey, setDrillKey] = useState<string | null>(null);
    const logFilterOpen = useLogFilterOpen();

    const { sortOptions: initialSortOptions } = useSearchSorts({
        category,
        sortBy: filterStates.sortBy,
        defaultSortBy: filterStates.defaultSortBy,
    });
    const [sortOptions, setSortOptions] =
        useState<SortOption[]>(initialSortOptions);
    useUpdateEffect(
        () => setSortOptions(initialSortOptions),
        [initialSortOptions],
    );

    // Re-seed the staged states from the live tab filters, and reset the
    // drill-in view + the staged sort, each time the sheet opens. Sort is only
    // committed on Apply, so without this reset a sort the user selected but
    // dismissed would survive the reopen and slip into the next Apply.
    useUpdateEffect(() => {
        if (open) {
            resetFilters(initialStates);
            setDrillKey(null);
            setSortOptions(initialSortOptions);
        }
    }, [open]);

    const {
        currentLocationDisabled,
        currentLocation,
        getCurrentLocation,
        loading: currentLocationLoading,
    } = useCurrentLocation();

    const loading = currentLocationLoading || aggregateLoading;

    const renderFilter = useCallback(
        (filter: FilterSectionFilterType): JSX.Element | null => {
            if (!enableAwardBadges && filter.kind === 'AwardsFilter') {
                return null;
            }
            // The category lives in the breadcrumb above the rows, never as a
            // drill-in row of its own.
            if (filter.kind === 'CategorySelector') return null;
            if (filter.kind === 'FilterGroup') {
                const childFilters = filter.filters.reduce((previous, f) => {
                    const childFilter = renderFilter(f);
                    if (childFilter) previous.push(childFilter);
                    return previous;
                }, [] as JSX.Element[]);
                if (!childFilters.length) return null;
                return (
                    <div
                        key={filter.key}
                        className={cn('flex flex-col', 'filter-' + filter.key)}
                        style={{ gap: resolveFlexGap(filter.gap) }}
                    >
                        {childFilters}
                    </div>
                );
            }

            const state =
                filter.filterKey === 'facetOptions'
                    ? filterStates[filter.filterKey]?.find(
                          (subState) => subState.id === filter.subFilterKey,
                      )?.state
                    : filterStates[filter.filterKey];
            if (
                !state ||
                typeof state === 'number' ||
                Array.isArray(state) ||
                (ignoreFilterKeys &&
                    ignoreFilterKeys.includes(filter.filterKey))
            )
                return null;

            return renderRefineControl(filter, state as FilterState, {
                filterStates,
                updateFilter,
                aggregate,
                loading,
                currentLocationDisabled,
                currentLocation,
                getCurrentLocation,
                marketingView,
            });
        },
        [
            filterStates,
            ignoreFilterKeys,
            updateFilter,
            currentLocationDisabled,
            currentLocation,
            getCurrentLocation,
            aggregate,
            loading,
            enableAwardBadges,
            marketingView,
        ],
    );

    // The facet + filter sections become drill-in rows (category is the
    // breadcrumb above, not a row). Config sections are relabelled + reordered
    // to match the category Refine.
    const normalizedSections = useMemo(
        () => normalizeRefineSections(filterSections),
        [filterSections],
    );
    const sections = useMemo<RefineSection[]>(() => {
        const built: RefineSection[] = [];

        const facetSections = createFacetFilterSections(
            filterStates.facetOptions,
        );
        [...normalizedSections, ...facetSections].forEach((section) => {
            if (section.adminOnly && !marketingView) return;
            const content = renderFilter(section.filter);
            if (!content) return;
            built.push({
                key: section.key ?? section.title,
                title: section.title,
                content,
                appliedCount: getAppliedFilterItems(
                    [section],
                    filterStates,
                    updateFilter,
                    ignoreFilterKeys,
                ).length,
            });
        });

        return built;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        category,
        filterStates,
        normalizedSections,
        marketingView,
        renderFilter,
        updateFilter,
        ignoreFilterKeys,
    ]);

    const drilledSection = drillKey
        ? (sections.find((section) => section.key === drillKey) ?? null)
        : null;

    // Clear the drill-in when its section disappears (e.g. a category change
    // drops a facet) so a later re-appearance can't silently re-open a drill-in
    // the user didn't choose — fall back to the root list instead.
    useEffect(() => {
        if (drillKey && !sections.some((section) => section.key === drillKey)) {
            setDrillKey(null);
        }
    }, [drillKey, sections]);

    // Every applied facet/filter value as a removable chip at the top (mirrors
    // the category Refine "Selected filters" row). Category is excluded — it has
    // its own breadcrumb.
    const appliedItems = useMemo(
        () =>
            getAppliedFilterItems(
                [
                    // Build chips from the *normalized* sections so a relabelled
                    // filter (e.g. isPurchasable → "Shop") reads the same on the
                    // chip as in the drill-in row. Category is excluded — it has
                    // its own breadcrumb, so it must not also appear as a chip.
                    ...normalizedSections.filter(
                        (section) => section.filter.kind !== 'CategorySelector',
                    ),
                    ...createFacetFilterSections(filterStates.facetOptions),
                ],
                filterStates,
                updateFilter,
                ignoreFilterKeys,
            ),
        [filterStates, normalizedSections, updateFilter, ignoreFilterKeys],
    );

    const clearAll = () =>
        resetFilters(resetFilterStates(filterStates, ignoreFilterKeys));

    // Category as a breadcrumb trail + drill-deeper pills, staged in place —
    // mirrors the category Refine sheet, on every tab.
    const categoryRoot = filterStates.category?.category as
        | Category
        | undefined;
    const selectedCategory = (filterStates.category?.selectedCategory ??
        null) as Category | null;
    const currentCategory = selectedCategory ?? categoryRoot;
    const currentChildren = currentCategory?.children ?? [];
    const rootLabel = ALL_LABEL[category ?? 'product'] ?? 'All';
    // Walk DOWN from the root through `.children` (the fully-populated tree) to
    // build the path to the current category. The `.parent` chain from
    // convertSearchCategory is only a one-level stub, so walking up would drop
    // intermediate crumbs on selections more than two levels deep.
    const categoryTrail: Category[] =
        categoryRoot && currentCategory
            ? (findCategoryPath(categoryRoot, currentCategory.id) ?? [
                  categoryRoot,
              ])
            : [];
    const stageCategory = (child: Category | null) => {
        updateFilter('category', {
            kind: 'CategorySelector',
            selectedCategory: child,
            isRoot: !child,
        } as Partial<CategoryFilterState>);
    };

    const [singular, plural] = RESULT_LABEL[category ?? 'product'] ?? [
        'result',
        'results',
    ];
    const totalCount = filterStates.totalCount;
    const ctaLabel =
        totalCount > 0
            ? `Show ${totalCount.toLocaleString()} ${totalCount === 1 ? singular : plural}`
            : 'No exact matches';

    const applyAndClose = () => {
        const selectedSort = sortOptions.find((option) => option.selected);
        filterStates.sortBy = selectedSort?.value;
        onApplyFilters?.(filterStates);
        onOpenChange(false);
    };

    // The applied-filter chips and the category breadcrumb are shown at the
    // Refine root AND retained above every drill-in, so the selection context
    // stays visible while choosing values (mirrors the category Refine sheet).
    const selectedFiltersSection =
        appliedItems.length > 0 ? (
            <section aria-label="Selected filters" className="flex flex-col">
                <div className="flex min-h-11 items-center justify-between gap-2">
                    <div className="font-sans text-base font-medium text-[#181818]">
                        Selected filters
                    </div>
                    <button
                        type="button"
                        onClick={clearAll}
                        className="shrink-0 font-sans text-sm text-[#6a6a6a] underline-offset-2 transition-colors hover:text-[#181818] hover:underline"
                    >
                        Clear all
                    </button>
                </div>
                <div className="-mx-5 flex [scrollbar-width:none] gap-2 overflow-x-auto px-5 pb-1 [&::-webkit-scrollbar]:hidden">
                    {appliedItems.map((item) => (
                        <button
                            key={item.key}
                            type="button"
                            aria-label={`Remove ${item.label}`}
                            onClick={item.action}
                            className="flex min-h-8 shrink-0 items-center gap-1.5 rounded-full bg-[#181818] px-3 py-1.5 font-sans text-sm text-white"
                        >
                            {item.label}
                            <X aria-hidden className="size-3.5" />
                        </button>
                    ))}
                </div>
            </section>
        ) : null;

    // Hide the block when there's nothing to act on — the trail is just the root
    // and there are no child pills — so it doesn't render a bare "Category"
    // heading with no controls (matches the category-page Refine tree).
    const showCategorySection =
        !!categoryRoot &&
        (currentChildren.length > 0 || categoryTrail.length > 1);
    const categorySection = showCategorySection ? (
        <section aria-label="Category" className="flex flex-col">
            <div className="flex min-h-11 items-center font-sans text-base font-medium text-[#181818]">
                Category
            </div>
            {categoryTrail.length > 1 ? (
                <nav
                    aria-label="Category trail"
                    className="flex flex-wrap items-center gap-x-1 font-sans text-sm"
                >
                    <button
                        type="button"
                        className="min-h-8 text-[#6a6a6a] underline-offset-2 hover:underline"
                        onClick={() => stageCategory(null)}
                    >
                        {rootLabel}
                    </button>
                    {categoryTrail.slice(1).map((node, index, arr) => {
                        const isCurrent = index === arr.length - 1;
                        const parent = arr[index - 1] ?? categoryRoot;
                        return (
                            <Fragment key={node.id}>
                                <ChevronRight
                                    aria-hidden
                                    className="size-3.5 shrink-0 text-[#9a9a9a]"
                                />
                                {isCurrent ? (
                                    <span
                                        aria-current="location"
                                        className="flex min-h-8 items-center gap-1 font-medium text-[#181818]"
                                    >
                                        {node.name}
                                        <button
                                            type="button"
                                            aria-label={`Remove category ${node.name}`}
                                            className="-m-1 p-2 text-[#6a6a6a] hover:text-[#181818]"
                                            onClick={() =>
                                                stageCategory(
                                                    parent.id ===
                                                        categoryRoot.id
                                                        ? null
                                                        : parent,
                                                )
                                            }
                                        >
                                            <X className="size-3.5" />
                                        </button>
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        className="min-h-8 text-[#6a6a6a] underline-offset-2 hover:underline"
                                        onClick={() => stageCategory(node)}
                                    >
                                        {node.name}
                                    </button>
                                )}
                            </Fragment>
                        );
                    })}
                </nav>
            ) : null}
            {currentChildren.length > 0 ? (
                <div className="flex flex-wrap gap-2 pt-1 pb-1">
                    {currentChildren.map((child) => (
                        <PillButton
                            key={child.id}
                            size="sm"
                            onClick={() => stageCategory(child)}
                        >
                            {child.name}
                        </PillButton>
                    ))}
                </div>
            ) : null}
        </section>
    ) : null;

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="bottom"
                color="opaque"
                hideDefaultCloseButton
                aria-label="Refine filters"
            >
                <div className="flex max-h-[85dvh] w-dvw flex-col overflow-hidden">
                    {/* Header: Refine (+ back when drilled in) + close. */}
                    <div className="relative flex min-h-14 items-center justify-center border-b border-black/10 px-5">
                        {drilledSection ? (
                            <button
                                type="button"
                                aria-label="Back to all filters"
                                onClick={() => setDrillKey(null)}
                                className="absolute left-3 flex size-10 items-center justify-center text-[#181818] opacity-70 transition-opacity hover:opacity-100"
                            >
                                <ChevronLeft className="size-6" />
                            </button>
                        ) : null}
                        <h2 className="m-0 font-sans text-lg font-medium text-[#181818]">
                            Refine
                        </h2>
                        <button
                            type="button"
                            aria-label="Close"
                            onClick={() => onOpenChange(false)}
                            className="absolute right-3 flex size-10 items-center justify-center text-[#181818] opacity-70 transition-opacity hover:opacity-100"
                        >
                            <X className="size-6" />
                        </button>
                    </div>

                    {/* Body — the drilled section, or the Refine root. */}
                    <div className="min-h-0 flex-1 overflow-y-auto px-5">
                        {drilledSection ? (
                            <div className="flex flex-col divide-y divide-black/10 *:py-3">
                                {selectedFiltersSection}
                                {categorySection}
                                <section aria-label={drilledSection.title}>
                                    <div className="flex min-h-11 items-center font-sans text-base font-medium text-[#181818]">
                                        {drilledSection.title}
                                    </div>
                                    {drilledSection.content}
                                </section>
                            </div>
                        ) : (
                            <div className="flex flex-col divide-y divide-black/10 *:py-3">
                                {selectedFiltersSection}
                                {categorySection}
                                {sortControlsEnabled &&
                                sortOptions.length > 0 ? (
                                    <section aria-label="Sort by">
                                        <div className="flex min-h-11 items-center font-sans text-base font-medium text-[#181818]">
                                            Sort by
                                        </div>
                                        <div className="-mx-5 flex [scrollbar-width:none] gap-2 overflow-x-auto px-5 pb-1 [&::-webkit-scrollbar]:hidden">
                                            {sortOptions.map((option) => (
                                                <PillButton
                                                    key={option.value}
                                                    size="sm"
                                                    selected={option.selected}
                                                    className="shrink-0"
                                                    onClick={() =>
                                                        setSortOptions((prev) =>
                                                            prev.map((o) => ({
                                                                ...o,
                                                                selected:
                                                                    o.value ===
                                                                    option.value,
                                                            })),
                                                        )
                                                    }
                                                >
                                                    {option.header}
                                                </PillButton>
                                            ))}
                                        </div>
                                    </section>
                                ) : null}

                                {sections.length > 0 ? (
                                    <section aria-label="Filters">
                                        <div className="flex min-h-11 items-center font-sans text-base font-medium text-[#181818]">
                                            Filters
                                        </div>
                                        {sections.map((section) => (
                                            <button
                                                key={section.key}
                                                type="button"
                                                onClick={() => {
                                                    setDrillKey(section.key);
                                                    logFilterOpen(
                                                        section.title,
                                                    );
                                                }}
                                                className="flex min-h-11 w-full items-center justify-between gap-3 border-b border-black/10 text-left last:border-b-0"
                                            >
                                                <span className="shrink-0 font-sans text-sm font-medium text-[#181818]">
                                                    {section.title}
                                                </span>
                                                <span className="flex min-w-0 items-center gap-2">
                                                    {section.appliedCount >
                                                    0 ? (
                                                        <span className="truncate font-sans text-sm text-[#9a9a9a]">
                                                            {
                                                                section.appliedCount
                                                            }{' '}
                                                            selected
                                                        </span>
                                                    ) : null}
                                                    <ChevronRight
                                                        aria-hidden
                                                        className="size-4 shrink-0 text-[#9a9a9a]"
                                                    />
                                                </span>
                                            </button>
                                        ))}
                                    </section>
                                ) : null}
                            </div>
                        )}
                    </div>

                    {/* Footer: the commit CTA. */}
                    <div className="border-t border-black/10 px-5 py-4">
                        <button
                            type="button"
                            onClick={applyAndClose}
                            disabled={loading || totalCount === 0}
                            className={cn(
                                'inline-flex h-[3.125rem] w-full items-center justify-center rounded-[var(--radius-btn-l)] bg-[#181818] font-sans text-sm font-medium text-[#f3f3f3] transition-colors hover:bg-[#2d2d2d] disabled:opacity-60',
                            )}
                        >
                            {ctaLabel}
                        </button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
};
